import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, DialogActions, DialogTitle, Typography, Stack, Box } from "@mui/material";
import { DeleteRounded } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { notify } from "../../../../../../shell/store/notifications";
import { DialogContent } from "@mui/material";
import { useDeleteRedirectMutation } from "../../../../../../shell/services/instance";
export type DeleteRedirectsProps = {
  ZUID: string;
  path: string;
};
export type DeleteDialogProps = {
  open: boolean;
  onClose: () => void;
  redirects: DeleteRedirectsProps[];
};
export const DeleteDialog: FC<DeleteDialogProps> = ({
  open,
  onClose,
  redirects
}) => {
  const {
    t
  } = useTranslation();
  const [deleteRedirect, {
    isLoading: isDeleting
  }] = useDeleteRedirectMutation();
  const dispatch = useDispatch();
  const descriptionPart = redirects?.length < 2 ? t("seo.deleteRedirectDescriptionSingular") : t("seo.deleteRedirectDescriptionPlural", {
    count: redirects?.length
  });
  const handleDeleteRedirects = async () => {
    const requests = [...(redirects || [])]?.map(redirect => deleteRedirect({
      ZUID: redirect?.ZUID
    }));
    Promise.allSettled(requests).then(res => {
      onClose();
    }).catch(() => {
      dispatch(notify({
        kind: "error",
        message: t("seo.errorDeletingRedirects")
      }));
    }).finally(() => {
      dispatch(notify({
        kind: "error",
        message: t("seo.redirectDeleted", {
          count: redirects?.length
        })
      }));
    });
  };
  return <Dialog open={open} fullWidth maxWidth="xs" onClose={onClose} data-sentry-element="Dialog" data-sentry-component="DeleteDialog" data-sentry-source-file="DeleteDialog.tsx">
      <DialogTitle data-sentry-element="DialogTitle" data-sentry-source-file="DeleteDialog.tsx">
        <Box sx={{
        backgroundColor: "red.100",
        borderRadius: "100%",
        width: "40px",
        height: "40px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        mb: 1.5
      }} data-sentry-element="Box" data-sentry-source-file="DeleteDialog.tsx">
          <DeleteRounded color="error" data-sentry-element="DeleteRounded" data-sentry-source-file="DeleteDialog.tsx" />
        </Box>
        <Stack display="flex" flexDirection="row" justifyContent="flex-start" alignItems="center" columnGap={1} overflow="hidden" textOverflow="ellipsis" data-sentry-element="Stack" data-sentry-source-file="DeleteDialog.tsx">
          <Typography data-cy="RedirectsDeleteDialogHeader" variant="inherit" fontWeight={700} flexGrow={0} flexShrink={0} data-sentry-element="Typography" data-sentry-source-file="DeleteDialog.tsx">
            {t("seo.deleteRedirectTitle", {
            count: redirects?.length
          })}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{
        mt: "8px"
      }} data-sentry-element="Typography" data-sentry-source-file="DeleteDialog.tsx">
          {`${descriptionPart} ${t("seo.deleteRedirectDescriptionSuffix", {
          count: redirects?.length
        })}`}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{
      p: 0
    }} data-cy="RedirectsDeleteDialog" data-sentry-element="DialogContent" data-sentry-source-file="DeleteDialog.tsx">
        <Box display="flex" flexDirection="column" justifyContent="flex-start" alignItems="flex-start" rowGap="20px" py={0} px="20px" data-sentry-element="Box" data-sentry-source-file="DeleteDialog.tsx">
          {redirects?.map((redirect, index) => <Box key={redirect?.ZUID} display="flex" flexDirection="row" justifyContent="flex-start" alignItems="baseline" columnGap="5px">
              <Typography variant="body2" color="info.dark" width="20px" flexGrow={0} flexShrink={0}>
                {`${index + 1}.`}
              </Typography>
              <Typography variant="body2" color="info.dark" flexGrow={1} flexShrink={1}>
                {redirect?.path}
              </Typography>
            </Box>)}
        </Box>
      </DialogContent>
      <DialogActions sx={{
      p: "20px"
    }} data-sentry-element="DialogActions" data-sentry-source-file="DeleteDialog.tsx">
        <Button variant="text" color="inherit" onClick={onClose} data-sentry-element="Button" data-sentry-source-file="DeleteDialog.tsx">
          {t("common.cancel")}
        </Button>
        <Button data-cy="DeleteContentItemConfirmButton" variant="contained" color="error" onClick={handleDeleteRedirects} loading={isDeleting} data-sentry-element="Button" data-sentry-source-file="DeleteDialog.tsx">
          {t("seo.deleteRedirectsButton", {
          count: redirects?.length
        })}
        </Button>
      </DialogActions>
    </Dialog>;
};